<template>
  <section class="ncr-messages-pane">
    <div v-if="errorMessage" class="ncr-pane-alert is-error">{{ errorMessage }}</div>
    <div v-if="noticeMessage" class="ncr-pane-alert is-notice">{{ noticeMessage }}</div>

    <div v-if="isLoading" class="ncr-pane-empty">Loading messages&hellip;</div>
    <div v-else-if="messages.length === 0" class="ncr-pane-empty">No messages yet.</div>
    <div v-else class="ncr-messages-list scroll-content">
      <article v-for="message in messages" :key="`${message.source}-${message.id}`" class="ncr-message">
        <img :src="avatarFor(message.authorId)" alt="" />
        <div class="ncr-message-copy">
          <header>
            <div class="ncr-message-author-line">
              <strong>{{ authorFor(message.authorId) }}</strong>
              <time>{{ formatDate(message.timestampMs) }}</time>
              <span v-if="message.legacy" class="ncr-message-legacy">Legacy note</span>
              <span v-if="message.edited" class="ncr-message-edited">Edited</span>
            </div>
            <OverflowMenu v-if="canShowMessageActions(message)" :key="`${message.id}-${messageMenuRenderKey}`"
              class="ncr-message-action-menu" button-label="Message actions" :portal="true" placement="top"
              :trigger-class="['ncr-message-action-trigger']" :options="getMessageMenuOptions(message)"
              @select="handleMessageMenuSelect($event, message)">
              <template #trigger><i class="material-icons">more_vert</i></template>
            </OverflowMenu>
          </header>
          <p class="ncr-message-body" @click="handleMessageContentClick" v-html="formatMessageHtml(message)"></p>
          <div v-if="message.attachments?.length" class="ncr-message-attachments">
            <button v-for="attachment in message.attachments" :key="`${message.id}-${attachment.fileId}`"
              type="button" :class="['ncr-message-attachment', `is-${attachment.previewKind}`]"
              :title="attachment.displayName" @click="openAttachment(attachment)">
              <img v-if="attachmentPreviewUrls[attachment.fileId]" :src="attachmentPreviewUrls[attachment.fileId]"
                :alt="`${attachment.displayName} preview`" loading="lazy" />
              <span v-else>{{ attachmentLabel(attachment) }}</span>
              <small>{{ attachment.displayName }}</small>
            </button>
          </div>
        </div>
      </article>
    </div>

    <MessageComposer v-if="!readonly" ref="composerRef" v-model="draft" class="ncr-message-composer"
      placeholder="Log notes, decisions, or analysis" :avatar-url="currentUserAvatar" :sending="isSending"
      :send-disabled="!canSend" :can-attach="canAttach" :has-attachments="draftAttachments.length > 0"
      :force-expanded="composerSuggestions.length > 0" :highlight-html="composerHighlightHtml"
      :is-editing="isEditing" link-aria-label="Link another NCR" link-tooltip="Link NCR" @submit="sendMessage"
      @cancel="cancelEditingMessage"
      @mention="insertComposerTokenAndBrowse('mention', '@')" @link="insertComposerTokenAndBrowse('ncr', '#')"
      @attach="openAttachmentPicker" @upload="openAttachmentPicker" @input="updateComposerSuggestions"
      @selection-change="updateComposerSuggestions" @keydown="handleComposerKeydown">
      <template #attachments>
        <div v-for="attachment in draftAttachments" :key="`draft-${attachment.fileId}`"
          :class="['composer-attachment-card', `is-${attachment.previewKind}`]">
          <img v-if="attachmentPreviewUrls[attachment.fileId]" :src="attachmentPreviewUrls[attachment.fileId]"
            :alt="`${attachment.displayName} preview`" class="composer-attachment-image" loading="lazy" />
          <span v-else class="composer-attachment-label">{{ attachmentLabel(attachment) }}</span>
          <span class="composer-attachment-name">{{ attachment.displayName }}</span>
          <button type="button" class="composer-attachment-remove" :aria-label="`Remove ${attachment.displayName}`"
            @click.stop="removeDraftAttachment(attachment.fileId)">
            <i class="material-icons">close</i>
          </button>
        </div>
        <div v-if="isUploadingAttachments" class="composer-attachment-card uploading is-file">
          <span class="composer-attachment-label">{{ uploadLabel }}</span>
          <div class="composer-attachment-progress-shell" aria-hidden="true">
            <div class="composer-attachment-progress-bar" :style="{ width: `${uploadProgress}%` }"></div>
          </div>
        </div>
      </template>

      <template #suggestions>
        <div v-if="composerSuggestions.length" class="composer-suggestion-list" role="listbox"
          aria-label="Message suggestions">
          <button v-for="(suggestion, index) in composerSuggestions" :key="suggestion.key" type="button"
            :class="['composer-suggestion-item', { active: index === highlightedSuggestionIndex }]"
            @mouseenter="highlightedSuggestionIndex = index" @mousedown.prevent="applyComposerSuggestion(index)">
            <img v-if="suggestion.kind === 'mention'" :src="suggestion.avatar" class="composer-suggestion-avatar" alt="" />
            <div v-else class="composer-suggestion-hash">#</div>
            <div class="composer-suggestion-line">
              <span class="composer-suggestion-label">{{ suggestion.label }}</span>
              <span v-if="suggestion.secondary" class="composer-suggestion-separator">&rsaquo;</span>
              <span v-if="suggestion.secondary" class="composer-suggestion-secondary">{{ suggestion.secondary }}</span>
            </div>
          </button>
        </div>
      </template>
    </MessageComposer>

    <input ref="attachmentInputRef" type="file" class="ncr-files-hidden-input" multiple
      @change="handleAttachmentInputChange" />
  </section>
</template>

<script setup lang="ts">
import { computed, nextTick, onBeforeUnmount, ref, watch } from 'vue';
import {
  collection,
  documentId,
  endAt,
  getDocs,
  limit,
  orderBy,
  query,
  startAt,
  type Unsubscribe
} from 'firebase/firestore';

import {
  MessageComposer,
  OverflowMenu,
  type MessageComposerExpose,
  type OverflowMenuOption
} from '@/components/ui';
import { fileClient, type RecordFile } from '@/modules/common/files/FileClient';
import {
  findDuplicateRecordFileUpload,
  getRecordFilePreviewKind,
  getRecordFilePreviewLabel,
  uploadRecordFiles,
  validateLocalRecordFile
} from '@/modules/common/files/RecordFileWorkflow';
import {
  messageClient,
  type MessageAttachment,
  type MessageEntityCandidates,
  type MessageMentionCandidate,
  type MessageNcrReferenceCandidate,
  type MessageRecord
} from '@/modules/common/messaging/MessageClient';
import { subscribeMessageFeed } from '@/modules/common/messaging/MessageFeed';
import { markSubjectNotificationsRead } from '@/modules/notifications/NotificationClient';
import { db } from '@/modules/firebase';
import { getCurrentUser } from '@/modules/firebase/Auth';
import { ncrClient } from '@/modules/ncr/NcrClient';
import { UserStore } from '@/modules/users/UserStore';

type ComposerTokenKind = 'mention' | 'ncr';

interface ComposerTokenContext {
  kind: ComposerTokenKind;
  query: string;
  start: number;
  end: number;
}

interface MentionSuggestion {
  kind: 'mention';
  key: string;
  uid: string;
  label: string;
  secondary: string;
  avatar: string;
}

interface NcrSuggestion {
  kind: 'ncr';
  key: string;
  ncrId: string;
  label: string;
  secondary: string;
}

type ComposerSuggestion = MentionSuggestion | NcrSuggestion;

const props = defineProps<{
  recordId: string;
  readonly?: boolean;
}>();

const messages = ref<MessageRecord[]>([]);
const draft = ref('');
const draftAttachments = ref<MessageAttachment[]>([]);
const selectedEntities = ref<MessageEntityCandidates>({ mentions: [], ncrs: [] });
const composerSuggestions = ref<ComposerSuggestion[]>([]);
const highlightedSuggestionIndex = ref(0);
const forcedSuggestionKind = ref<ComposerTokenKind | null>(null);
const isLoading = ref(false);
const isSending = ref(false);
const isUploadingAttachments = ref(false);
const editingMessageId = ref('');
const deletingMessageId = ref('');
const messageMenuRenderKey = ref(0);
const uploadProgress = ref(0);
const uploadLabel = ref('Uploading');
const errorMessage = ref('');
const noticeMessage = ref('');
const attachmentPreviewUrls = ref<Record<string, string>>({});
const composerRef = ref<MessageComposerExpose | null>(null);
const attachmentInputRef = ref<HTMLInputElement | null>(null);
let unsubscribes: Unsubscribe[] = [];
let loadGeneration = 0;
let suggestionGeneration = 0;

const currentUserAvatar = computed(() => {
  const user = getCurrentUser();
  return user?.slack?.avatar
    || user?.photoURL
    || UserStore.getAvatar(user?.uid || user?.email || user?.displayName)
    || '/assets/img/avatar.png';
});

const canAttach = computed(() => Boolean(props.recordId) && !isSending.value && !isUploadingAttachments.value
  && !editingMessageId.value && draftAttachments.value.length < 3);

const isEditing = computed(() => Boolean(editingMessageId.value));

const canSend = computed(() => Boolean(props.recordId)
  && !isSending.value
  && !isUploadingAttachments.value
  && (draft.value.trim().length > 0 || draftAttachments.value.length > 0));

const composerHighlightHtml = computed(() => formatComposerHtml(draft.value));

function escapeHtml(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

function escapeAttribute(value: string): string {
  return escapeHtml(value).replace(/`/g, '&#96;');
}

function plainHtml(value: string): string {
  return escapeHtml(value).replace(/\n/g, '<br>');
}

function getComposerElement(): HTMLTextAreaElement | null {
  return composerRef.value?.getTextareaElement() || null;
}

function formatComposerHtml(value: string): string {
  if (!value) return '';

  const labels = [
    ...(selectedEntities.value.mentions || []).map(entity => ({ label: entity.label || '', kind: 'mention' })),
    ...(selectedEntities.value.ncrs || []).map(entity => ({ label: entity.label || '', kind: 'ncr' }))
  ].filter(entity => entity.label && value.includes(entity.label));

  if (!labels.length) {
    return plainHtml(value);
  }

  const ranges = labels
    .map(entity => ({ ...entity, start: value.indexOf(entity.label), end: value.indexOf(entity.label) + entity.label.length }))
    .sort((left, right) => left.start - right.start);
  let cursor = 0;
  let html = '';

  for (const range of ranges) {
    if (range.start < cursor) continue;
    html += plainHtml(value.slice(cursor, range.start));
    html += `<span class="composer-entity composer-entity-${range.kind}">${plainHtml(value.slice(range.start, range.end))}</span>`;
    cursor = range.end;
  }

  return html + plainHtml(value.slice(cursor));
}

function formatMessageHtml(message: MessageRecord): string {
  const text = message.text || message.lines.join('\n');
  const entities = [
    ...(message.entities?.mentions || []).map(entity => ({ ...entity, kind: 'mention' as const })),
    ...(message.entities?.ncrs || []).map(entity => ({ ...entity, kind: 'ncr' as const }))
  ]
    .filter(entity => Number.isInteger(entity.start) && Number.isInteger(entity.end))
    .sort((left, right) => Number(left.start) - Number(right.start));

  if (!entities.length) return plainHtml(text);

  let cursor = 0;
  let html = '';
  for (const entity of entities) {
    const start = Number(entity.start);
    const end = Number(entity.end);
    if (start < cursor || end <= start || end > text.length) continue;

    html += plainHtml(text.slice(cursor, start));
    const entityText = plainHtml(text.slice(start, end));
    if (entity.kind === 'mention') {
      html += `<span class="ncr-message-entity is-mention" data-uid="${escapeAttribute(entity.uid)}">${entityText}</span>`;
    } else {
      html += `<a class="ncr-message-entity is-ncr" href="/ncr2/${encodeURIComponent(entity.ncrId)}" data-ncr-id="${escapeAttribute(entity.ncrId)}">${entityText}</a>`;
    }
    cursor = end;
  }

  return html + plainHtml(text.slice(cursor));
}

function handleMessageContentClick(event: MouseEvent): void {
  const target = event.target instanceof Element ? event.target.closest<HTMLElement>('[data-ncr-id]') : null;
  const ncrId = target?.dataset.ncrId;
  if (!ncrId) return;

  event.preventDefault();
  void window.NcrModalController?.edit(ncrId);
}

function stopSubscription(): void {
  loadGeneration += 1;
  unsubscribes.forEach(unsubscribe => unsubscribe());
  unsubscribes = [];
}

function startSubscription(recordId: string): void {
  stopSubscription();
  resetComposer();
  messages.value = [];
  errorMessage.value = '';
  isLoading.value = Boolean(recordId);
  void UserStore.init();
  if (!recordId) return;
  void markSubjectNotificationsRead('ncr', recordId).catch(error => {
    console.warn('[NcrMessagesPane] Unable to reconcile notification read state:', error);
  });

  const generation = loadGeneration;
  const isCurrent = () => generation === loadGeneration && recordId === props.recordId;
  const user = getCurrentUser();
  unsubscribes = [subscribeMessageFeed(
    'ncr', recordId, {
      uid: user?.uid || '',
      isAdmin: user?.permissions?.adminAccess === true
    },
    nextMessages => {
      if (!isCurrent()) return;
      messages.value = nextMessages.sort((left, right) => left.timestampMs - right.timestampMs);
      isLoading.value = false;
      nextMessages.flatMap(message => message.attachments || []).forEach(attachment => {
        void ensureAttachmentPreview(attachment);
      });
    },
    error => {
      if (!isCurrent()) return;
      errorMessage.value = error.message || 'Failed to load messages';
      isLoading.value = false;
    },
    100
  )];
}

async function sendMessage(): Promise<void> {
  if (!canSend.value) return;

  const text = draft.value.trim();
  const draftSnapshot = draft.value;
  const attachmentSnapshot = [...draftAttachments.value];
  const entitiesSnapshot: MessageEntityCandidates = {
    mentions: [...(selectedEntities.value.mentions || [])],
    ncrs: [...(selectedEntities.value.ncrs || [])]
  };
  const targetMessageId = editingMessageId.value;

  isSending.value = true;
  errorMessage.value = '';
  noticeMessage.value = '';
  try {
    const entityCandidates = buildEntityCandidates(text);
    const attachmentCandidates = draftAttachments.value.map(attachment => ({ fileId: attachment.fileId }));
    const savedMessage = targetMessageId
      ? await messageClient.updateMessage(
        'ncr',
        props.recordId,
        targetMessageId,
        text,
        entityCandidates,
        attachmentCandidates
      )
      : await messageClient.createMessage(
        'ncr',
        props.recordId,
        text,
        entityCandidates,
        attachmentCandidates
      );
    resetComposer();
    noticeMessage.value = (savedMessage.warnings || []).map(warning => warning.message).join(' ');
  } catch (error) {
    draft.value = draftSnapshot;
    draftAttachments.value = attachmentSnapshot;
    selectedEntities.value = entitiesSnapshot;
    errorMessage.value = error instanceof Error ? error.message : 'Failed to send message';
  } finally {
    isSending.value = false;
  }
}

async function removeMessage(message: MessageRecord): Promise<void> {
  if (!message.actionState?.canDelete || deletingMessageId.value === message.id) return;
  if (!window.confirm('Delete this message?')) return;

  deletingMessageId.value = message.id;
  closeMessageMenu();
  try {
    await messageClient.deleteMessage('ncr', props.recordId, message.id);
  } catch (error) {
    errorMessage.value = error instanceof Error ? error.message : 'Failed to delete message';
  } finally {
    deletingMessageId.value = '';
  }
}

function canShowMessageActions(message: MessageRecord): boolean {
  return !props.readonly && (message.actionState?.canEdit === true || message.actionState?.canDelete === true);
}

function getMessageMenuOptions(message: MessageRecord): OverflowMenuOption[] {
  return [
    {
      key: 'edit',
      label: 'Edit',
      icon: 'edit',
      disabled: message.actionState?.canEdit !== true || isSending.value
    },
    {
      key: 'delete',
      label: 'Delete',
      icon: 'delete',
      tone: 'danger',
      disabled: message.actionState?.canDelete !== true || deletingMessageId.value === message.id
    }
  ];
}

function handleMessageMenuSelect(actionKey: string, message: MessageRecord): void {
  if (actionKey === 'edit') {
    startEditingMessage(message);
  } else if (actionKey === 'delete') {
    void removeMessage(message);
  }
}

function startEditingMessage(message: MessageRecord): void {
  if (message.actionState?.canEdit !== true || isSending.value) return;

  closeMessageMenu();
  editingMessageId.value = message.id;
  draft.value = message.text;
  draftAttachments.value = [...(message.attachments || [])];
  selectedEntities.value = {
    mentions: (message.entities?.mentions || []).map(entity => ({
      uid: entity.uid,
      label: entity.label,
      start: entity.start ?? undefined,
      end: entity.end ?? undefined
    })),
    ncrs: (message.entities?.ncrs || []).map(entity => ({
      ncrId: entity.ncrId,
      label: entity.label,
      start: entity.start ?? undefined,
      end: entity.end ?? undefined
    }))
  };
  errorMessage.value = '';
  void nextTick(() => {
    composerRef.value?.focus();
    const end = draft.value.length;
    composerRef.value?.setSelectionRange(end, end);
  });
}

function cancelEditingMessage(): void {
  resetComposer();
  errorMessage.value = '';
}

function closeMessageMenu(): void {
  messageMenuRenderKey.value += 1;
}

function resetComposer(): void {
  draft.value = '';
  draftAttachments.value = [];
  selectedEntities.value = { mentions: [], ncrs: [] };
  composerSuggestions.value = [];
  highlightedSuggestionIndex.value = 0;
  forcedSuggestionKind.value = null;
  editingMessageId.value = '';
}

function openAttachmentPicker(): void {
  if (canAttach.value) attachmentInputRef.value?.click();
}

async function handleAttachmentInputChange(event: Event): Promise<void> {
  const input = event.target as HTMLInputElement;
  const files = Array.from(input.files || []);
  input.value = '';
  if (!files.length) return;

  const remainingSlots = 3 - draftAttachments.value.length;
  if (files.length > remainingSlots) {
    noticeMessage.value = `A message can include up to 3 files. You can add ${remainingSlots} more.`;
    return;
  }

  const validationError = files.map(validateLocalRecordFile).find(Boolean);
  if (validationError) {
    errorMessage.value = validationError;
    return;
  }

  const duplicate = findDuplicateRecordFileUpload(files, draftAttachments.value);
  if (duplicate) {
    noticeMessage.value = `${duplicate} is already attached to this draft.`;
    return;
  }

  isUploadingAttachments.value = true;
  uploadProgress.value = 0;
  uploadLabel.value = files.length === 1 ? files[0].name : `${files.length} files`;
  errorMessage.value = '';
  try {
    const uploadedFiles = await uploadRecordFiles({
      recordType: 'ncr',
      recordId: props.recordId,
      files,
      onProgress: progress => {
        uploadProgress.value = progress.percent;
      }
    });

    for (const file of uploadedFiles) {
      const attachment = toMessageAttachment(file);
      draftAttachments.value.push(attachment);
      await ensureAttachmentPreview(attachment);
      const downloadUrl = await fileClient.getDownloadUrl(file.storagePath);
      await ncrClient.addModernFileMirror(props.recordId, file, downloadUrl);
    }
  } catch (error) {
    errorMessage.value = error instanceof Error ? error.message : 'Failed to upload attachment';
  } finally {
    isUploadingAttachments.value = false;
    uploadProgress.value = 0;
  }
}

function toMessageAttachment(file: RecordFile): MessageAttachment {
  return {
    fileId: file.id,
    displayName: file.displayName,
    originalName: file.originalName,
    mimeType: file.mimeType,
    extension: file.extension,
    sizeBytes: file.sizeBytes,
    storagePath: file.storagePath,
    version: file.version,
    previewKind: getRecordFilePreviewKind(file)
  };
}

function removeDraftAttachment(fileId: string): void {
  draftAttachments.value = draftAttachments.value.filter(attachment => attachment.fileId !== fileId);
}

function attachmentLabel(attachment: MessageAttachment): string {
  return getRecordFilePreviewLabel(attachment);
}

async function ensureAttachmentPreview(attachment: MessageAttachment): Promise<void> {
  if (attachment.previewKind !== 'image' || attachmentPreviewUrls.value[attachment.fileId]) return;
  try {
    const url = await fileClient.getDownloadUrl(attachment.storagePath);
    attachmentPreviewUrls.value = { ...attachmentPreviewUrls.value, [attachment.fileId]: url };
  } catch (error) {
    console.warn('[NcrMessagesPane] Attachment preview unavailable:', error);
  }
}

async function openAttachment(attachment: MessageAttachment): Promise<void> {
  try {
    const url = await fileClient.getDownloadUrl(attachment.storagePath);
    window.open(url, '_blank', 'noopener');
  } catch (error) {
    errorMessage.value = error instanceof Error ? error.message : 'Unable to open attachment';
  }
}

async function insertComposerTokenAndBrowse(kind: ComposerTokenKind, token: string): Promise<void> {
  const textarea = getComposerElement();
  const caret = textarea?.selectionStart ?? draft.value.length;
  const existing = extractTokenContext(draft.value, caret);
  if (existing?.kind === kind) {
    forcedSuggestionKind.value = kind;
    await updateComposerSuggestions();
    composerRef.value?.focus();
    return;
  }

  const before = draft.value.slice(0, caret);
  const after = draft.value.slice(caret);
  const insertion = `${before && !/\s$/.test(before) ? ' ' : ''}${token}`;
  draft.value = `${before}${insertion}${after}`;
  const nextCaret = caret + insertion.length;
  forcedSuggestionKind.value = kind;

  await nextTick();
  composerRef.value?.focus();
  composerRef.value?.setSelectionRange(nextCaret, nextCaret);
  await updateComposerSuggestions();
}

function extractTokenContext(text: string, caret: number): ComposerTokenContext | null {
  const beforeCaret = text.slice(0, caret);
  const match = beforeCaret.match(/(^|[\s\n])([@#][^\s@#]*)$/);
  if (!match) return null;

  const token = match[2];
  return {
    kind: token.startsWith('@') ? 'mention' : 'ncr',
    query: token.slice(1),
    start: beforeCaret.length - token.length,
    end: beforeCaret.length
  };
}

async function updateComposerSuggestions(): Promise<void> {
  const textarea = getComposerElement();
  const token = textarea ? extractTokenContext(draft.value, textarea.selectionStart ?? draft.value.length) : null;
  if (!token) {
    composerSuggestions.value = [];
    forcedSuggestionKind.value = null;
    pruneSelectedEntities();
    return;
  }

  const generation = ++suggestionGeneration;
  if (token.kind === 'mention') {
    const queryValue = token.query.trim();
    if (queryValue.length < 2 && forcedSuggestionKind.value !== 'mention') {
      composerSuggestions.value = [];
      return;
    }

    const users = forcedSuggestionKind.value === 'mention' && !queryValue
      ? Object.values(UserStore.getAllUsers()).filter(user => Boolean(user.uid))
      : UserStore.searchUsers(queryValue);
    const nextSuggestions: ComposerSuggestion[] = users
      .slice()
      .sort((left, right) => left.display_name.localeCompare(right.display_name))
      .slice(0, 20)
      .map(user => ({
        kind: 'mention',
        key: `mention-${user.uid}`,
        uid: user.uid as string,
        label: user.display_name.startsWith('@') ? user.display_name : `@${user.display_name}`,
        secondary: user.email || '',
        avatar: user.avatar
      }));
    if (generation !== suggestionGeneration) return;
    composerSuggestions.value = nextSuggestions;
  } else {
    try {
      const nextSuggestions = await loadNcrSuggestions(token.query, forcedSuggestionKind.value === 'ncr');
      if (generation !== suggestionGeneration) return;
      composerSuggestions.value = nextSuggestions;
    } catch (error) {
      if (generation !== suggestionGeneration) return;
      composerSuggestions.value = [];
      errorMessage.value = error instanceof Error ? error.message : 'Unable to load NCR suggestions';
    }
  }

  highlightedSuggestionIndex.value = 0;
}

async function loadNcrSuggestions(searchValue: string, showFullList: boolean): Promise<NcrSuggestion[]> {
  const trimmed = searchValue.trim().toUpperCase();
  const prefix = trimmed && !trimmed.startsWith('NCR-')
    ? `NCR-${trimmed.replace(/^NCR/, '').replace(/^-/, '')}`
    : trimmed;

  if (!prefix && !showFullList) return [];

  const ncrQuery = prefix
    ? query(collection(db, 'ncr'), orderBy(documentId()), startAt(prefix), endAt(`${prefix}\uf8ff`), limit(20))
    : query(collection(db, 'ncr'), orderBy('datetime', 'desc'), limit(25));
  const snapshot = await getDocs(ncrQuery);

  return snapshot.docs
    .filter(entry => entry.id !== props.recordId)
    .map(entry => {
      const data = entry.data();
      return {
        kind: 'ncr',
        key: `ncr-${entry.id}`,
        ncrId: entry.id,
        label: `#${entry.id}`,
        secondary: [data.newpart || data.partno, data.status].filter(Boolean).join(' • ')
      };
    });
}

function applyComposerSuggestion(index: number): void {
  const suggestion = composerSuggestions.value[index];
  const textarea = getComposerElement();
  if (!suggestion || !textarea) return;

  const token = extractTokenContext(draft.value, textarea.selectionStart ?? draft.value.length);
  if (!token) return;

  const insertion = `${suggestion.label} `;
  draft.value = `${draft.value.slice(0, token.start)}${insertion}${draft.value.slice(token.end)}`;
  if (suggestion.kind === 'mention') {
    selectedEntities.value.mentions = [
      ...(selectedEntities.value.mentions || []).filter(entity => entity.uid !== suggestion.uid),
      { uid: suggestion.uid, label: suggestion.label }
    ];
  } else {
    selectedEntities.value.ncrs = [
      ...(selectedEntities.value.ncrs || []).filter(entity => entity.ncrId !== suggestion.ncrId),
      { ncrId: suggestion.ncrId, label: suggestion.label }
    ];
  }

  composerSuggestions.value = [];
  forcedSuggestionKind.value = null;
  const nextCaret = token.start + insertion.length;
  void nextTick(() => {
    composerRef.value?.focus();
    composerRef.value?.setSelectionRange(nextCaret, nextCaret);
  });
}

function pruneSelectedEntities(): void {
  selectedEntities.value = {
    mentions: (selectedEntities.value.mentions || []).filter(entity => entity.label && draft.value.includes(entity.label)),
    ncrs: (selectedEntities.value.ncrs || []).filter(entity => entity.label && draft.value.includes(entity.label))
  };
}

function buildEntityCandidates(text: string): MessageEntityCandidates | undefined {
  pruneSelectedEntities();
  const mentions = (selectedEntities.value.mentions || []).map(entity => withTextRange(entity, text));
  const ncrs = (selectedEntities.value.ncrs || []).map(entity => withTextRange(entity, text));

  const knownMentionUids = new Set(mentions.map(entity => entity.uid));
  const aliases = new Map<string, string>();
  UserStore.getAllProfiles().forEach(profile => {
    [profile.slack?.display_name, profile.slack?.real_name, profile.displayName, profile.email?.split('@')[0]]
      .filter((alias): alias is string => Boolean(alias?.trim()))
      .forEach(alias => aliases.set(alias.trim().toLowerCase(), profile.uid));
  });

  for (const match of text.matchAll(/(^|[\s\n])@([^\s@#]+)/g)) {
    const uid = aliases.get(match[2].toLowerCase());
    if (!uid || knownMentionUids.has(uid)) continue;
    const start = (match.index || 0) + match[1].length;
    const label = text.slice(start, start + match[0].trim().length);
    mentions.push({ uid, label, start, end: start + label.length });
    knownMentionUids.add(uid);
  }

  const knownNcrs = new Set(ncrs.map(entity => entity.ncrId));
  for (const match of text.matchAll(/#(NCR-\d+)/gi)) {
    const ncrId = match[1].toUpperCase();
    if (knownNcrs.has(ncrId)) continue;
    ncrs.push({ ncrId, label: match[0], start: match.index, end: (match.index || 0) + match[0].length });
    knownNcrs.add(ncrId);
  }

  return mentions.length || ncrs.length ? { mentions, ncrs } : undefined;
}

function withTextRange<T extends MessageMentionCandidate | MessageNcrReferenceCandidate>(entity: T, text: string): T {
  const label = entity.label || '';
  const start = text.indexOf(label);
  return { ...entity, start, end: start >= 0 ? start + label.length : start };
}

function handleComposerKeydown(event: KeyboardEvent): void {
  if (composerSuggestions.value.length) {
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      highlightedSuggestionIndex.value = (highlightedSuggestionIndex.value + 1) % composerSuggestions.value.length;
      return;
    }
    if (event.key === 'ArrowUp') {
      event.preventDefault();
      highlightedSuggestionIndex.value = (highlightedSuggestionIndex.value - 1 + composerSuggestions.value.length)
        % composerSuggestions.value.length;
      return;
    }
    if ((event.key === 'Enter' || event.key === 'Tab') && composerSuggestions.value[highlightedSuggestionIndex.value]) {
      event.preventDefault();
      applyComposerSuggestion(highlightedSuggestionIndex.value);
      return;
    }
    if (event.key === 'Escape') {
      event.preventDefault();
      composerSuggestions.value = [];
      forcedSuggestionKind.value = null;
      return;
    }
  }

  if (event.key === 'Enter' && !event.shiftKey) {
    event.preventDefault();
    void sendMessage();
  }
}

function authorFor(authorId: string): string {
  const user = UserStore.getUserByUid(authorId) || UserStore.getUserByName(authorId);
  return user?.display_name || authorId || 'Unknown user';
}

function avatarFor(authorId: string): string {
  const user = UserStore.getUserByUid(authorId) || UserStore.getUserByName(authorId);
  return user?.avatar || '/assets/img/avatar.png';
}

function formatDate(timestampMs: number): string {
  return timestampMs ? new Date(timestampMs).toLocaleString() : '';
}

watch(() => props.recordId, startSubscription, { immediate: true });
onBeforeUnmount(stopSubscription);
</script>
